import { useRouter } from 'next/router'
import Image from 'next/image'
// Utils
import { capitalizeFirstLetterOfTitle, findWord } from '@/utils/converter'
import {
  getCoverImages,
  getTitleAndDescription,
  hasBreedAndStateQuery,
} from './util/helper'
import styles from './LandingCover.module.scss'
import { Trustpilot } from '../trustpilot/trustpilot-landing'
import { FC } from 'react'

const ROOT_URL = process.env.NEXT_PUBLIC_ROOT_URL

const StepLink = ({ href, children }) => (
  <a className={styles.step} href={href}>
    {children}
  </a>
)
const ArrowRight = () => <span className={styles.arrowRight} />

const badges = [
  {
    id: 1,
    image: '/landing/responsible_breeder.svg',
    text: 'Responsible Breeders',
  },
  {
    id: 2,
    image: '/landing/concierge.svg',
    text: 'Excellent Customer Service',
  },
  {
    id: 3,
    image: '/landing/travel.svg',
    text: 'Nationwide Travel',
  },
]

const Badge = ({ image, text }) => {
  return (
    <div className={styles.badge}>
      <Image height={60} width={60} src={image} alt={text} />
      <h3>{text}</h3>
    </div>
  )
}

const Breadcrumbs = ({ landingType, variables, category, breed, city }) => {
  const router = useRouter()
  const isBreedAndState = hasBreedAndStateQuery(router)

  return (
    <p className={styles.steps}>
      <StepLink href={`${ROOT_URL}/puppies-for-sale`}>
        Puppies for Sale
      </StepLink>
      {landingType === 'category' && (
        <>
          <ArrowRight /> {capitalizeFirstLetterOfTitle(category)}
        </>
      )}
      {breed?.slug && !isBreedAndState && (
        <>
          <ArrowRight />
          {breed.name}
        </>
      )}
      {variables?.stateName && !isBreedAndState && (
        <>
          <ArrowRight />
          {variables?.stateName}
        </>
      )}
      {variables?.stateName && landingType === 'stateCity' && (
        <>
          <ArrowRight />
          <StepLink href={`${ROOT_URL}/puppies-for-sale/${variables?.slug}`}>
            {variables?.stateName}
          </StepLink>
          <ArrowRight /> {city}
        </>
      )}

      {isBreedAndState &&
        landingType !== 'breedCity' &&
        landingType !== 'stateCity' && (
          <>
            <ArrowRight />
            <StepLink href={`${ROOT_URL}/puppies-for-sale/${breed.slug}`}>
              {breed.name}
            </StepLink>
            <ArrowRight /> {variables?.stateName}
          </>
        )}
      {isBreedAndState && landingType === 'breedCity' && (
        <>
          <ArrowRight />
          <StepLink href={`${ROOT_URL}/puppies-for-sale/${breed.slug}`}>
            {breed.name}
          </StepLink>
          <ArrowRight />
          <StepLink
            href={`${ROOT_URL}/puppies-for-sale/${breed.slug}/${variables?.stateSlug}`}
          >
            {variables?.stateName}
          </StepLink>
          <ArrowRight /> {city}
        </>
      )}
    </p>
  )
}

const ContentContainer = ({
  title,
  description,
  coverImage,
  mobileCoverImage,
  landingType,
  variables,
  breed,
  category,
  categoryName,
  city,
}) => {
  const altImg =
    landingType === 'breedState'
      ? `Dogs in ${variables?.stateName}`
      : breed?.name
        ? `${findWord(breed?.name)} dog`
        : category

  const mobileImgProps = {
    width: 414,
    height: 190,
    quality: 90,
    src: mobileCoverImage,
    alt: altImg,
    priority: true,
  }

  const desktopImgProps = {
    width: 391,
    height: 370,
    quality: 90,
    src: coverImage,
    alt: altImg,
    priority: true,
  }

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          landingType={landingType}
          variables={variables}
          category={categoryName}
          breed={breed}
          city={city}
        />
      </div>
      <div className={styles.cardsContainer}>
        <div className={styles.breedCardSummary}>
          <h1>{title}</h1>
          <p className={styles.description}>{description}</p>
          <div className={styles.badgeContainer}>
            {badges?.map((badge) => (
              <Badge key={badge.id} image={badge.image} text={badge.text} />
            ))}
          </div>
          <div className={styles.trustpilot}>
            <Trustpilot />
          </div>
        </div>

        {/* Mobile image */}
        <div className={styles.mobileCoverImg}>
          <Image {...mobileImgProps} alt="mobile landing cover image" />
        </div>
        {/* Desktop Image */}
        <div className={styles.desktopImageHolder}>
          <Image {...desktopImgProps} alt="desktop landing cover" />
        </div>
      </div>
    </div>
  )
}

interface LandingCoverProps {
  breed?: any
  category?: string
  categoryName?: string
  state?: string
  landingType?: string
  variables?: any
  city?: string
}

const LandingCover: FC<LandingCoverProps> = ({
  breed,
  category,
  categoryName,
  state,
  landingType,
  variables,
  city,
}) => {
  const { coverImage, mobileCoverImage } = getCoverImages(
    category,
    breed,
    state,
  )
  const { title, description } = getTitleAndDescription(
    landingType,
    breed,
    categoryName,
    variables,
    city,
  )

  return (
    <div className={styles.landingCoverContainer}>
      <div className={styles.backgroundCover} />
      <ContentContainer
        title={title}
        description={description}
        coverImage={coverImage}
        mobileCoverImage={mobileCoverImage}
        landingType={landingType}
        variables={variables}
        breed={breed}
        category={category}
        categoryName={categoryName}
        city={city}
      />
    </div>
  )
}

export default LandingCover
